/**
 * Outil « Rectangles contre trapèzes » — calculs (pur).
 *
 * Aire sous la courbe approchée par n rectangles (point gauche, droit ou
 * milieu) ou n trapèzes, comparée à l'intégrale EXACTE (primitive connue).
 * L'erreur décroît en 1/n pour les rectangles gauche/droite, en 1/n² pour
 * le milieu et les trapèzes : doubler n divise l'erreur par 2 ou par 4.
 */

export type FnId = 'square' | 'sqrt' | 'exp' | 'inv' | 'sin';
export type Method = 'left' | 'right' | 'mid' | 'trap';

export interface Preset {
  a: number;
  b: number;
  label: string;
  /** intégrale exacte en LaTeX et en texte, sa valeur, ses atomes pour la feuille */
  tex: string;
  plain: string;
  value: number;
  atoms: string[];
}

export interface RiemannFn {
  id: FnId;
  label: string;
  tex: string;
  f: (x: number) => number;
  /** primitive écrite : « \frac{x^3}{3} » */
  primitiveTex: string;
  presets: Preset[];
}

const PI = Math.PI;

export const FUNCS: readonly RiemannFn[] = [
  {
    id: 'square',
    label: 'x²',
    tex: 'x^2',
    f: (x) => x * x,
    primitiveTex: '\\frac{x^3}{3}',
    presets: [
      {
        a: 0,
        b: 1,
        label: '[0 ; 1]',
        tex: '\\frac{1}{3}',
        plain: '1/3',
        value: 1 / 3,
        atoms: ['1', '÷', '3'],
      },
      {
        a: 0,
        b: 2,
        label: '[0 ; 2]',
        tex: '\\frac{8}{3}',
        plain: '8/3',
        value: 8 / 3,
        atoms: ['8', '÷', '3'],
      },
      { a: -1, b: 2, label: '[−1 ; 2]', tex: '3', plain: '3', value: 3, atoms: ['3'] },
    ],
  },
  {
    id: 'sqrt',
    label: '√x',
    tex: '\\sqrt{x}',
    f: (x) => Math.sqrt(x),
    primitiveTex: '\\frac{2}{3}x\\sqrt{x}',
    presets: [
      {
        a: 0,
        b: 1,
        label: '[0 ; 1]',
        tex: '\\frac{2}{3}',
        plain: '2/3',
        value: 2 / 3,
        atoms: ['2', '÷', '3'],
      },
      {
        a: 0,
        b: 4,
        label: '[0 ; 4]',
        tex: '\\frac{16}{3}',
        plain: '16/3',
        value: 16 / 3,
        atoms: ['16', '÷', '3'],
      },
      {
        a: 1,
        b: 9,
        label: '[1 ; 9]',
        tex: '\\frac{52}{3}',
        plain: '52/3',
        value: 52 / 3,
        atoms: ['52', '÷', '3'],
      },
    ],
  },
  {
    id: 'exp',
    label: 'eˣ',
    tex: 'e^x',
    f: Math.exp,
    primitiveTex: 'e^x',
    presets: [
      {
        a: 0,
        b: 1,
        label: '[0 ; 1]',
        tex: 'e - 1',
        plain: 'e − 1',
        value: Math.E - 1,
        atoms: ['exp', '(', '1', ')', '−', '1'],
      },
      {
        a: -1,
        b: 1,
        label: '[−1 ; 1]',
        tex: 'e - e^{-1}',
        plain: 'e − e⁻¹',
        value: Math.E - 1 / Math.E,
        atoms: ['exp', '(', '1', ')', '−', 'exp', '(', '−', '1', ')'],
      },
      {
        a: 0,
        b: 2,
        label: '[0 ; 2]',
        tex: 'e^2 - 1',
        plain: 'e² − 1',
        value: Math.E ** 2 - 1,
        atoms: ['exp', '(', '2', ')', '−', '1'],
      },
    ],
  },
  {
    id: 'inv',
    label: '1/x',
    tex: '\\frac{1}{x}',
    f: (x) => 1 / x,
    primitiveTex: '\\ln x',
    presets: [
      {
        a: 1,
        b: 2,
        label: '[1 ; 2]',
        tex: '\\ln 2',
        plain: 'ln 2',
        value: Math.LN2,
        atoms: ['ln', '(', '2', ')'],
      },
      {
        a: 1,
        b: 4,
        label: '[1 ; 4]',
        tex: '\\ln 4',
        plain: 'ln 4',
        value: Math.log(4),
        atoms: ['ln', '(', '4', ')'],
      },
      {
        a: 1,
        b: 3,
        label: '[1 ; 3]',
        tex: '\\ln 3',
        plain: 'ln 3',
        value: Math.log(3),
        atoms: ['ln', '(', '3', ')'],
      },
    ],
  },
  {
    id: 'sin',
    label: 'sin x',
    tex: '\\sin x',
    f: Math.sin,
    primitiveTex: '-\\cos x',
    presets: [
      { a: 0, b: PI / 2, label: '[0 ; π/2]', tex: '1', plain: '1', value: 1, atoms: ['1'] },
      { a: 0, b: PI, label: '[0 ; π]', tex: '2', plain: '2', value: 2, atoms: ['2'] },
      { a: 0, b: 2 * PI, label: '[0 ; 2π]', tex: '0', plain: '0', value: 0, atoms: ['0'] },
    ],
  },
];

export function fnById(id: FnId): RiemannFn {
  return FUNCS.find((f) => f.id === id)!;
}

/** Somme approchée de ∫ₐᵇ f par n rectangles ou trapèzes. */
export function approx(
  f: (x: number) => number,
  a: number,
  b: number,
  n: number,
  m: Method,
): number {
  const h = (b - a) / n;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const x0 = a + i * h;
    const x1 = x0 + h;
    if (m === 'left') s += f(x0);
    else if (m === 'right') s += f(x1);
    else if (m === 'mid') s += f((x0 + x1) / 2);
    else s += (f(x0) + f(x1)) / 2;
  }
  return s * h;
}

/** Les morceaux à dessiner : rectangles (hauteur) ou trapèzes (deux hauteurs). */
export function shapes(
  f: (x: number) => number,
  a: number,
  b: number,
  n: number,
  m: Method,
): Array<{ x0: number; x1: number; y0: number; y1: number }> {
  const h = (b - a) / n;
  return Array.from({ length: n }, (_, i) => {
    const x0 = a + i * h;
    const x1 = x0 + h;
    if (m === 'trap') return { x0, x1, y0: f(x0), y1: f(x1) };
    const y = m === 'left' ? f(x0) : m === 'right' ? f(x1) : f((x0 + x1) / 2);
    return { x0, x1, y0: y, y1: y };
  });
}

/**
 * Seuil sous lequel un écart est un pur bruit d'arrondi : la somme est alors
 * EXACTE (sin x sur une période : les morceaux se compensent).
 */
export function exactTolerance(p: Preset): number {
  return 1e-12 * Math.max(1, Math.abs(p.value), p.b - p.a);
}

/** f(a) = f(b) : rectangles à gauche, à droite et trapèzes donnent la même somme. */
export function sameEnds(fn: RiemannFn, p: Preset): boolean {
  return Math.abs(fn.f(p.a) - fn.f(p.b)) < exactTolerance(p);
}

/** Erreurs pour n, 2n, 4n, 8n et le rapport d'une ligne à la suivante (≈ 2 ou ≈ 4). */
export function errorTable(
  fn: RiemannFn,
  p: Preset,
  m: Method,
  n0 = 10,
): Array<{ n: number; error: number; ratio: number | null }> {
  const tol = exactTolerance(p);
  const rows: Array<{ n: number; error: number; ratio: number | null }> = [];
  for (let k = 0; k < 4; k++) {
    const n = n0 * 2 ** k;
    const raw = Math.abs(approx(fn.f, p.a, p.b, n, m) - p.value);
    const error = raw < tol ? 0 : raw;
    const prev = rows[k - 1];
    rows.push({ n, error, ratio: prev && prev.error > 0 && error > 0 ? prev.error / error : null });
  }
  return rows;
}
